'use client'

import {Link} from '@/src/shared/ui/link'
import {Image} from '@/src/shared/ui/image'

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Col, Collapse, Flex, Popover, Row} from 'antd'
import {Routes} from '@/src/config/routes'
import * as UI from '@/src/shared/ui/layout/ui/header/header.styles'
import * as MenuUI from './deep-menu.styled'

import iconMenu from '@/src/shared/assets/media/icon-menu.svg'
import iconSearch from '@/src/shared/assets/media/icon-search.svg'
import {usePathname, useSearchParams} from 'next/navigation'
import {useRouter} from 'nextjs-toploader/app'

import {observer} from 'mobx-react'
import {useLayoutStore} from '@/src/shared/ui/layout'
import first from 'lodash/first'
import {createGlobalStyle} from 'styled-components'
import {BodySelector} from '@/src/config/settings'
import {ChevronRight, SquareMinus, SquarePlus} from 'lucide-react'
import noPhotoPic from '@/src/shared/assets/media/no-photo.png'
import {TooltipPlacement} from 'antd/es/tooltip'

interface UseMenuHoverOptions {
  hoverDelay?: number // задержка перед активацией (опционально)
  leaveDelay?: number // задержка перед деактивацией (для плавности)
  defaultMenuId?: string
}

export const useMenuHover = (options: UseMenuHoverOptions = {}) => {
  const {hoverDelay = 0, leaveDelay = 100, defaultMenuId = ''} = options

  const [menuId, setMenuId] = useState(defaultMenuId)

  const [isActive, setIsActive] = useState(true)
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const contentRef = useRef<HTMLDivElement>(null)

  // Очистка таймаутов при размонтировании
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
      if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current)
    }
  }, [])

  const handleMouseEnter = useCallback(
    (buttonElement: HTMLElement) => {
      // if (hoverDelay == 0) {
      //   setPendingMenuId(buttonElement.dataset.menuId)
      // } else {
      //   setMenuId(buttonElement.dataset.menuId)
      // }

      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current)
        leaveTimeoutRef.current = null
      }

      if (hoverDelay > 0) {
        hoverTimeoutRef.current = setTimeout(() => {
          setIsActive(true)
          setMenuId(buttonElement.dataset.menuId!)
        }, hoverDelay)
      } else {
        setIsActive(true)
        setMenuId(buttonElement.dataset.menuId!)
      }
    },
    [hoverDelay],
  )

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }

    if (leaveDelay > 0) {
      leaveTimeoutRef.current = setTimeout(() => {
        setIsActive(false)
      }, leaveDelay)
    } else {
      setIsActive(false)
    }
  }, [leaveDelay])

  return {
    contentRef,
    isActive,
    menuId,
    handleMouseEnter,
    handleMouseLeave,
  }
}

const MenuComponent = observer(() => {
  const rootStore = useLayoutStore()

  const {handleMouseEnter, isActive, menuId} = useMenuHover({
    defaultMenuId: first(rootStore.data.header.catalog)?.id,
  })

  const catalogActiveSubsections = useMemo(() => {
    return rootStore.data.header.catalog.find(section => section.id === menuId)?.children ?? []
  }, [rootStore.data.header.catalog, menuId])

  return (
    <MenuUI.PopoverContent>
      <Row wrap={false} style={{height: '100%', maxHeight: 'calc(100vh - 150px)'}}>
        <Flex gap={33} style={{width: '100%'}}>
          <MenuUI.CategoryCol as={Col} flex="0 0 auto">
            <MenuUI.CategoryFlex as={Flex} gap={6} vertical>
              <MenuUI.CategoryTitle>Категории</MenuUI.CategoryTitle>
              {rootStore.data.header.catalog.map(catalogSection => (
                <MenuUI.SectionButton
                  asChild
                  href={Routes.catalog.section.page(catalogSection.path)}
                  key={catalogSection.id}
                  onMouseEnter={e => handleMouseEnter(e.currentTarget)}
                  data-menu-id={catalogSection.id}
                  data-active={menuId === catalogSection.id}>
                  <MenuUI.SectionImage>
                    <Image
                      width={60}
                      height={60}
                      src={catalogSection.images.length > 0 ? catalogSection.images[0]! : noPhotoPic}
                    />
                  </MenuUI.SectionImage>

                  <span>{catalogSection.name}</span>

                  {catalogSection.children.length > 0 && <ChevronRight size={16} />}
                </MenuUI.SectionButton>
              ))}
            </MenuUI.CategoryFlex>
          </MenuUI.CategoryCol>
          {isActive && <MenuCatalogContent catalogSections={catalogActiveSubsections} />}
        </Flex>
      </Row>
    </MenuUI.PopoverContent>
  )
})

const CatalogDeepMenuStyles = createGlobalStyle`
  ${BodySelector} .catalog-deep-menu {
    .ant-menu-item,
    .ant-menu-vertical > .ant-menu-submenu > .ant-menu-submenu-title {
      height: unset;
      line-height: 1.5;
      padding-left: 4px;
      padding-top: 6px;
      padding-bottom: 6px;
      font-size: 14px;

      span {
        coror: gray;
      }
    }
  }

  ${BodySelector} .catalog-deep-menu.ant-menu,
  ${BodySelector} .catalog-deep-menu.ant-menu-submenu {
    border: unset !important;
    border-right: unset !important;
    border-left: unset !important;
  }
`

export const CatalogDeepMenu: React.FC = () => {
  const [open, setOpen] = useState(false)
  const {ref, placement, updatePlacement} = useSmartPlacement()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const [popoverContainer, setPopoverContainer] = useState()

  useEffect(() => {
    setOpen(false)
  }, [pathname, searchParams])

  return (
    <>
      <CatalogDeepMenuStyles />
      <Popover
        open={open}
        getPopupContainer={popoverContainer ? () => popoverContainer : undefined}
        rootClassName="header-catalog-popover"
        placement={placement}
        content={<MenuComponent />}
        arrow={false}
        onOpenChange={open => {
          setOpen(open)
          if (open) {
            requestAnimationFrame(updatePlacement)
          }
        }}>
        <UI.ButtonWithIcon ref={ref} onClick={() => router.push(Routes.catalog.page)}>
          <UI.Icon title="Каталог">
            <UI.IconMenu src={iconMenu} width={17} height={15} alt="" />
            <UI.IconSearch src={iconSearch} width={10} height={10} alt="" />
          </UI.Icon>
          <span>Каталог</span>
        </UI.ButtonWithIcon>
      </Popover>
      <PopoverContainer onMount={setPopoverContainer} />
    </>
  )
}

const PopoverContainer = ({onMount}: any) => {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (ref.current) {
      onMount(ref.current)
    }
  }, [ref.current])

  return <MenuUI.PopoverContainer ref={ref} />
}

function useSmartPlacement() {
  const ref = useRef<HTMLButtonElement | null>(null)
  const [placement, setPlacement] = useState<TooltipPlacement>('bottomLeft')

  const updatePlacement = () => {
    if (!ref.current) {
      return
    }

    const rect = ref.current.getBoundingClientRect()
    if (!rect) return

    const overflowRight = rect.right + 1240 > window.innerWidth

    if (overflowRight) {
      setPlacement('bottom') // fallback
    } else {
      setPlacement('bottomLeft')
    }
  }

  return {ref, placement, updatePlacement}
}

export const MenuCatalogContent = ({catalogSections, deph = 1}: {catalogSections: HeaderCatalogSection[], deph?: number}) => {
  const {handleMouseEnter, isActive, menuId} = useMenuHover({
    // hoverDelay: 300,
    // leaveDelay: 500,
    // defaultMenuId: _.first(catalogSections)?.id,
  })

  const catalogActiveSubsections = useMemo(() => {
    if (!menuId) {
      return []
    }

    return catalogSections.find(section => section.id === menuId)?.children ?? []
  }, [catalogSections, isActive, menuId])

  if (deph === 3) {
    return (
      <MenuUI.Col as={Col}>
        <SectionChildPopover childs={catalogSections} />
      </MenuUI.Col>
    )
  }

  return (
    <>
      <MenuUI.Col as={Col}>
        <MenuUI.ColFlex as={Flex} flex="1 1 auto" gap={6} vertical>
          {catalogSections.map(catalogSection => (
            // <MenuUI.SectionGroup>
            //   <MenuUI.SectionGroupTitle></MenuUI.SectionGroupTitle>
            // </MenuUI.SectionGroup>

            <MenuUI.SubSectionButton
              asChild
              href={Routes.catalog.section.page(catalogSection.path)}
              key={catalogSection.id}
              onMouseEnter={e => handleMouseEnter(e.currentTarget)}
              data-menu-id={catalogSection.id}
              data-active={menuId === catalogSection.id}>
              <MenuUI.SectionImage>
                <Image
                  width={60}
                  height={60}
                  src={catalogSection.images.length > 0 ? catalogSection.images[0]! : noPhotoPic}
                />
              </MenuUI.SectionImage>

              <span>{catalogSection.name}</span>
              {catalogSection.children.length > 0 && <ChevronRight size={16} />}
            </MenuUI.SubSectionButton>
          ))}
        </MenuUI.ColFlex>
      </MenuUI.Col>
      {isActive && catalogActiveSubsections.length > 0 && (
        <MenuCatalogContent catalogSections={catalogActiveSubsections} deph={deph + 1} />
      )}
    </>
    // <MenuUI.CatalogComponent>
    //   <SectionChildPopover childs={catalogSubSections} root />
    // </MenuUI.CatalogComponent>
  )
}

const SectionChildPopover = ({childs}: {childs: any[]}) => {
  return <NestedSectionCollapse sections={childs} />
}

const NestedSectionCollapse = ({sections}: any) => {
  return <SubSectionCollapse sections={sections} />
}

const SubSectionCollapse = ({sections, deth = 1}: {sections: HeaderCatalogSection[]; deth?: number}) => {
  const [activeKey, setActiveKey] = useState<string | null>(null)

  return (
    <MenuUI.SectionCollapse
      as={Collapse}
      activeKey={activeKey ?? []}
      expandIcon={() => null}
      items={sections.map(section => ({
        label: (deth === 1 && (
          <MenuUI.SectionCollapseLabel>
            <Link href={Routes.catalog.section.page(section.path)}>{section.name}</Link>
            {section.children.length > 0 && (
              <MenuUI.ExpandIcon
                as={activeKey === null ? SquarePlus : SquareMinus}
                data-active={activeKey === `${deth}-${section.id}`}
                size={16}
                onClick={() =>
                  activeKey === `${deth}-${section.id}`
                    ? setActiveKey(null)
                    : setActiveKey(`${deth}-${section.id}`)
                }
              />
            )}
          </MenuUI.SectionCollapseLabel>
        )) || (
          <MenuUI.SubSectionButton asChild key={section.id}>
            <MenuUI.SectionImage>
              <Image
                width={60}
                height={60}
                src={section.images.length > 0 ? section.images[0]! : noPhotoPic}
              />
            </MenuUI.SectionImage>
            <Link href={Routes.catalog.section.page(section.path)}>{section.name}</Link>

            {section.children.length > 0 && (
              <MenuUI.ExpandIcon
                as={activeKey === null ? SquarePlus : SquareMinus}
                data-active={activeKey === `${deth}-${section.id}`}
                size={16}
                onClick={() =>
                  activeKey === `${deth}-${section.id}`
                    ? setActiveKey(null)
                    : setActiveKey(`${deth}-${section.id}`)
                }
              />
            )}
          </MenuUI.SubSectionButton>
        ),
        key: `${deth}-${section.id}`,
        children:
          section.children.length > 0 ? (
            <SubSectionCollapse sections={section.children} deth={deth + 1} />
          ) : undefined,
      }))}
    />
  )
}
